import React, { useMemo, useEffect, useState } from 'react';
import { useApp } from '../../../context/AppContext';
import { apiService } from '../../../services/api';
import StatCard from '../../../components/StatCard/StatCard';
import PageHeader from '../../../components/PageHeader/PageHeader';
import './Dashboard.css';

const ManagerDashboard = () => {
  // Provide safe defaults so component doesn't crash when context values are missing
  const {
    orders = [],
    currentUser = null,
    pumps
  } = useApp() || {};


  // Local state for manager daily records from API
  const [managerDailyRecords, setManagerDailyRecords] = useState({});
  const [loadingDailyRecords, setLoadingDailyRecords] = useState(false);

  // ---- Fetch manager daily records from API ----
  useEffect(() => {
    const fetchManagerDailyRecords = async () => {
      try {
        setLoadingDailyRecords(true);
        // apiService.getManagerDailyRecords already returns response.data?.data
        const data = await apiService.getManagerDailyRecords();
        setManagerDailyRecords(data ?? {});
      } catch (err) {
        console.error('Error fetching manager daily records:', err);
        setManagerDailyRecords({});
      } finally {
        setLoadingDailyRecords(false);
      }
    };

    fetchManagerDailyRecords();
  }, []);

  // Normalize pumps into an array regardless of shape
  const pumpsData = useMemo(() => {
    if (Array.isArray(pumps)) return pumps;
    if (pumps && Array.isArray(pumps.data)) return pumps.data;
    return [];
  }, [pumps]);

  // Get current manager's pump (safe)
  const currentPump = useMemo(() => {
    if (!currentUser?.phone) return null;
    const phone = String(currentUser.phone).trim();
    return pumpsData.find(
      (p) =>
        String(p.managerPhone ?? p.manager_phone ?? p.manager?.phone ?? '')
          .trim() === phone
    );
  }, [pumpsData, currentUser?.phone]);


  // mdr is the normalized object from API
  // API response.data example:
  // {
  //   "orders_today": 1,
  //   "pending_orders_today": 0,
  //   "completed_orders_today": 0,
  //   "todays_quantity": 0,
  //   "todays_revenue": 0
  // }
  const mdr = useMemo(() => {
    if (Array.isArray(managerDailyRecords)) return managerDailyRecords[0] ?? {};
    return managerDailyRecords ?? {};
  }, [managerDailyRecords]);

  const stats = useMemo(() => {
    const todayOrders = Number(mdr.orders_today ?? 0) || 0;
    const pendingOrders = Number(mdr.pending_orders_today ?? 0) || 0;
    const completedOrders = Number(mdr.completed_orders_today ?? 0) || 0;
    const todayVolume = Number(mdr.todays_quantity ?? 0) || 0;
    const todaysRevenue = Number(mdr.todays_revenue ?? 0) || 0;

    return {
      todayOrders,
      pendingOrders,
      completedOrders,
      todayVolume,
      todaysRevenue,
    };
  }, [mdr]);

  // Helper to safely format rupee value
  const formatINR = (value) => {
    const n = Number(value) || 0;
    try {
      return n.toLocaleString('en-IN');
    } catch {
      return String(n);
    }
  };

  return (
    <div className="manager-dashboard">
      <PageHeader
        title="Manager Dashboard"
        subtitle={`Welcome, ${currentUser?.name || 'Manager'}`}
      />

      <div className="dashboard-meta">
        {currentPump ? (
          <div className="current-pump">
            <strong>Pump:</strong>{' '}
            {currentPump.pumpName ||
              currentPump.brand ||
              currentPump.name ||
              'Unnamed'}
            {currentPump.city ? ` — ${currentPump.city}` : ''}
          </div>
        ) : (
          <div className="current-pump"></div>
        )}
      </div>

      <div className="dashboard-stats-grid">
        <StatCard
          title="Today's Orders"
          value={stats.todayOrders}
          subtitle="Orders received today"
          icon="📦"
          loading={loadingDailyRecords}
        />
        <StatCard
          title="Pending Orders"
          value={stats.pendingOrders}
          subtitle="Awaiting action"
          icon="⏳"
          loading={loadingDailyRecords}
        />
        <StatCard
          title="Completed Orders"
          value={stats.completedOrders}
          subtitle="Successfully completed"
          icon="✅"
          loading={loadingDailyRecords}
        />
        <StatCard
          title="Today's Quantity"
          value={`${stats.todayVolume} L`}
          subtitle="Liters delivered today"
          icon="⛽"
          loading={loadingDailyRecords}
        />
        <StatCard
          title="Today's Revenue"
          value={`₹${formatINR(stats.todaysRevenue)}`}
          subtitle="Revenue from today"
          icon="💰"
          loading={loadingDailyRecords}
        />
      </div>
    </div>
  );
};

export default ManagerDashboard;
